import React, { useState, useMemo } from 'react';
import { User, Key, Eye, EyeOff, AlertCircle, Video, Clock, Calendar, TrendingUp, RefreshCw, Ticket } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useChangePassword } from '../hooks/useUsers';
import { useUserStats } from '../hooks/useUserStats';
import { useQueryClient } from 'react-query';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmationDialog from '../components/ConfirmationDialog';
import toast from 'react-hot-toast';
import { useTickets } from '../hooks/useTickets';
import { isAdmin, isCustomerSupport, isVideoRecorder, normalizeStatus } from '../utils/roles';

interface VideoStats {
  totalVideos: number;
  totalDuration: number;
  completedVideos: number;
  inProgressVideos: number;
  plannedVideos: number;
  averageDuration: number;
  thisWeekVideos: number;
  thisMonthVideos: number;
}

const Profile: React.FC = () => {
  const { user, completePasswordChange } = useAuth();
  const changePasswordMutation = useChangePassword();
  const queryClient = useQueryClient();
  
  const supportView = isCustomerSupport(user?.role);
  const recorderView = isVideoRecorder(user?.role);
  const adminView = isAdmin(user?.role);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  // Admins: system-wide video stats; everyone else: personal scope
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useUserStats(
    adminView ? undefined : 'user'
  );
  // Fetch tickets for customer support users
  const { data: ticketsData, isLoading: ticketsLoading } = useTickets();
  
  // Filter tickets for customer support (only tickets they created)
  const myTickets = useMemo(() => {
    const tickets = ticketsData?.tickets || [];
    if (supportView && user) {
      return tickets.filter(ticket => ticket.requestedBy?.id === user.id);
    }
    return [];
  }, [ticketsData, supportView, user]);

  // Calculate ticket statistics for customer support
  const ticketStats = useMemo(() => {
    if (!supportView) return null;

    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const norm = (status?: string) => normalizeStatus(status);

    return {
      totalTickets: myTickets.length,
      openTickets: myTickets.filter((t) => norm(t.status) === 'open').length,
      pipelineTickets: myTickets.filter((t) => {
        const s = norm(t.status);
        return s === 'accepted' || s === 'recording' || s === 'processing';
      }).length,
      readyToSendTickets: myTickets.filter((t) => norm(t.status) === 'completed').length,
      sentTickets: myTickets.filter((t) => norm(t.status) === 'sent').length,
      cancelledTickets: myTickets.filter((t) => norm(t.status) === 'cancelled').length,
      failedTickets: myTickets.filter((t) => norm(t.status) === 'failed').length,
      thisWeekTickets: myTickets.filter((t) => new Date(t.createdAt) >= weekAgo).length,
      thisMonthTickets: myTickets.filter((t) => new Date(t.createdAt) >= monthAgo).length,
    };
  }, [myTickets, supportView]);

  // Refresh statistics
  const handleRefreshStats = () => {
    if (supportView) {
      console.log('Profile - Refreshing ticket statistics...');
      queryClient.invalidateQueries('tickets');
      toast.success('Statistics refreshed');
    } else {
      console.log('Profile - Refreshing video statistics...');
      queryClient.invalidateQueries('user-stats');
      refetchStats();
      toast.success('Statistics refreshed');
    }
  };

  const recording = stats?.recording;
  const videoStats: VideoStats = {
    totalVideos: recording?.sessionCount ?? 0,
    totalDuration: recording?.totalDurationSeconds ?? 0,
    completedVideos: recording?.completedSessions ?? 0,
    inProgressVideos: recording?.inProgressSessions ?? 0,
    plannedVideos: recording?.plannedSessions ?? 0,
    averageDuration: recording?.averageDurationSeconds ?? 0,
    thisWeekVideos: recording?.thisWeekSessions ?? 0,
    thisMonthVideos: recording?.thisMonthSessions ?? 0,
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }

    try {
      const result = await changePasswordMutation.mutateAsync({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      if (result.token && result.user) {
        completePasswordChange(result.token, result.user);
      }
      toast.success('Password updated successfully');
      setShowPasswordDialog(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to update password');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadgeColor = (role: string) => {
    const r = role?.toLowerCase() ?? '';
    if (r === 'admin' || r === 'administrator' || r === 'video_admin') {
      return 'bg-purple-100 text-purple-800 border-purple-200';
    }
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const roleDisplayLabel = (role: string | undefined) => {
    const r = role?.toLowerCase() ?? '';
    if (r === 'admin' || r === 'administrator') return 'Administrator';
    if (r === 'video_admin') return 'Video admin';
    if (r === 'game_master') return 'Game master';
    return role ? role.replace(/_/g, ' ') : 'User';
  };

  return (
    <div className="etr-page">
      {/* Header */}
      <div className="etr-page-header">
        <h1 className="etr-page-title flex items-center">
          <User className="h-8 w-8 mr-3 text-blue-600" />
          Profile
        </h1>
        <p className="etr-page-subtitle">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* User Information */}
        <div className="etr-surface p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <div className="text-lg text-gray-900">
                {user?.firstName} {user?.lastName}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="text-lg text-gray-900">{user?.email}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getRoleBadgeColor(user?.role || 'user')}`}>
                {roleDisplayLabel(user?.role)}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
              <div className="text-lg text-gray-900">
                {user?.createdAt ? formatDate(user.createdAt) : 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="etr-surface p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Security</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Password</h3>
                <p className="text-sm text-gray-600">Last updated: Recently</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPasswordDialog(true)}
                leftIcon={<Key className="h-4 w-4" />}
              >
                Change Password
              </Button>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-500 mr-3 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">Security Tips</h4>
                  <ul className="mt-1 text-sm text-blue-700 space-y-1">
                    <li>• Use a strong, unique password</li>
                    <li>• Don't share your password with others</li>
                    <li>• Log out when using shared devices</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section - Video Stats or Ticket Stats */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            {supportView ? (
              <>
                <Ticket className="h-6 w-6 mr-3 text-blue-600" />
                Your Ticket Statistics
              </>
            ) : (
              <>
                <Video className="h-6 w-6 mr-3 text-blue-600" />
                {adminView ? 'All Video Statistics' : 'Your Video Statistics'}
              </>
            )}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshStats}
            leftIcon={<RefreshCw className="h-4 w-4" />}
            disabled={supportView ? ticketsLoading : statsLoading}
          >
            {supportView ? (ticketsLoading ? 'Refreshing...' : 'Refresh') : (statsLoading ? 'Refreshing...' : 'Refresh')}
          </Button>
        </div>
        
        {supportView ? (
          // Ticket Statistics for Customer Support
          ticketsLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600">Loading your ticket statistics...</span>
            </div>
          ) : ticketStats ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Tickets */}
              <div className="etr-surface p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Ticket className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Tickets</p>
                    <p className="text-2xl font-bold text-gray-900">{ticketStats.totalTickets}</p>
                  </div>
                </div>
              </div>

              <div className="etr-surface p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-8 w-8 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Open</p>
                    <p className="text-2xl font-bold text-gray-900">{ticketStats.openTickets}</p>
                  </div>
                </div>
              </div>

              <div className="etr-surface p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrendingUp className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">In pipeline</p>
                    <p className="text-2xl font-bold text-gray-900">{ticketStats.pipelineTickets}</p>
                  </div>
                </div>
              </div>

              <div className="etr-surface p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Calendar className="h-8 w-8 text-emerald-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Sent</p>
                    <p className="text-2xl font-bold text-gray-900">{ticketStats.sentTickets}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null
        ) : (
          // Video Statistics for other users
          statsLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600">
                {adminView ? 'Loading portal video statistics...' : 'Loading your video statistics...'}
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Videos */}
              <div className="etr-surface p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Video className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      {adminView ? 'Total sessions' : 'Total Videos'}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">{videoStats.totalVideos}</p>
                  </div>
                </div>
              </div>

              {/* Total Duration */}
              <div className="etr-surface p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Duration</p>
                    <p className="text-2xl font-bold text-gray-900">{formatDuration(videoStats.totalDuration)}</p>
                  </div>
                </div>
              </div>

              {/* Completed Videos */}
              <div className="etr-surface p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrendingUp className="h-8 w-8 text-emerald-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Completed</p>
                    <p className="text-2xl font-bold text-gray-900">{videoStats.completedVideos}</p>
                  </div>
                </div>
              </div>

              {/* Average Duration */}
              <div className="etr-surface p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Calendar className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Avg Duration</p>
                    <p className="text-2xl font-bold text-gray-900">{formatDuration(videoStats.averageDuration)}</p>
                  </div>
                </div>
              </div>
            </div>
          )
        )}

        {/* Additional Statistics */}
        {supportView ? (
          // Ticket Statistics Breakdown for Customer Support
          ticketStats && ticketStats.totalTickets > 0 && (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Status Breakdown */}
              <div className="etr-surface p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Open</span>
                    <span className="font-semibold text-yellow-600">{ticketStats.openTickets}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">In pipeline</span>
                    <span className="font-semibold text-blue-600">{ticketStats.pipelineTickets}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Ready to send</span>
                    <span className="font-semibold text-violet-600">{ticketStats.readyToSendTickets}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Sent</span>
                    <span className="font-semibold text-emerald-600">{ticketStats.sentTickets}</span>
                  </div>
                  {ticketStats.cancelledTickets > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Cancelled</span>
                      <span className="font-semibold text-gray-600">{ticketStats.cancelledTickets}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="etr-surface p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">This Week</span>
                    <span className="font-semibold text-blue-600">{ticketStats.thisWeekTickets} tickets</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">This Month</span>
                    <span className="font-semibold text-blue-600">{ticketStats.thisMonthTickets} tickets</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Completion Rate</span>
                    <span className="font-semibold text-emerald-600">
                      {ticketStats.totalTickets > 0 ? Math.round((ticketStats.sentTickets / ticketStats.totalTickets) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : (
          // Video Statistics Breakdown for other users
          !statsLoading && videoStats.totalVideos > 0 && (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Status Breakdown */}
              <div className="etr-surface p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Completed</span>
                    <span className="font-semibold text-emerald-600">{videoStats.completedVideos}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">In Progress</span>
                    <span className="font-semibold text-yellow-600">{videoStats.inProgressVideos}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Planned</span>
                    <span className="font-semibold text-blue-600">{videoStats.plannedVideos}</span>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="etr-surface p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">This Week</span>
                    <span className="font-semibold text-blue-600">{videoStats.thisWeekVideos} videos</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">This Month</span>
                    <span className="font-semibold text-blue-600">{videoStats.thisMonthVideos} videos</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Completion Rate</span>
                    <span className="font-semibold text-emerald-600">
                      {videoStats.totalVideos > 0 ? Math.round((videoStats.completedVideos / videoStats.totalVideos) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions - Hidden for customer support */}
              {!supportView && (
                <div className="etr-surface p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    {!recorderView && (
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-full"
                        onClick={() => window.location.href = '/sessions/new'}
                      >
                        Create New Video
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => window.location.href = '/sessions'}
                    >
                      View All Videos
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )
        )}

        {/* Empty State */}
        {supportView ? (
          // Empty state for customer support (tickets)
          !ticketsLoading && ticketStats && ticketStats.totalTickets === 0 && (
            <div className="mt-12 bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <Ticket className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Tickets Yet</h3>
              <p className="text-gray-600 mb-6">
                You haven't created any tickets yet. Start by creating a ticket for a recording session.
              </p>
              <Button
                variant="primary"
                size="lg"
                onClick={() => window.location.href = '/tickets'}
                leftIcon={<Ticket className="h-5 w-5" />}
              >
                Create Your First Ticket
              </Button>
            </div>
          )
        ) : (
          // Empty state for other users (videos)
          !statsLoading && videoStats.totalVideos === 0 && (
            <div className="mt-12 bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <Video className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Videos Yet</h3>
              <p className="text-gray-600 mb-6">
                {recorderView 
                  ? "You don't have any videos yet. Create videos from your assigned tickets."
                  : "Start your ETR exposure therapy journey by creating your first video."}
              </p>
              {!recorderView && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => window.location.href = '/sessions/new'}
                  leftIcon={<Video className="h-5 w-5" />}
                >
                  Create Your First Video
                </Button>
              )}
            </div>
          )
        )}
      </div>

      {/* Change Password Dialog */}
      <ConfirmationDialog
        isOpen={showPasswordDialog}
        onClose={() => {
          setShowPasswordDialog(false);
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
        }}
        onConfirm={handleChangePassword}
        title="Change Password"
        message="Enter your current password and choose a new password:"
        confirmText="Update Password"
        cancelText="Cancel"
        variant="info"
        isLoading={changePasswordMutation.isLoading}
        customContent={
          <div className="mt-4 space-y-4">
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter new password (min 6 characters)"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default Profile;


